import React, { useRef, useState, useEffect, useCallback } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { isEmpty, isNil } from 'ramda';

// api
import AuthenticationService from '../../service/authentication-service';

// tools
import { isEmail } from '../../helpers/validators';

// store
import { storeSet, useStoreValue } from '../../store';

// components
import FormField from '../../components/FormField';
import PasswordChecklist from '../../components/PasswordChecklist';
import LogoIcon from '../../icons/LogoIcon';
import { Button } from 'primereact/button';
import { Messages } from 'primereact/messages';
import { Panel } from 'primereact/panel';
import { Dialog } from 'primereact/dialog';
import { useSearchParams } from 'react-router-dom';

const APP_HUB_ID = process.env.REACT_APP_HUB_ID;

const Registration = () => {
    const token = useStoreValue('token');
    const [searchParams] = useSearchParams();
    let invitationData = searchParams.get('data');
    if (invitationData) {
        invitationData = atob(invitationData);
        const searchParams = new URLSearchParams(invitationData);
        invitationData = Object.fromEntries(searchParams)
    }
    const [loading, setLoading] = useState(false);
    const [visibleTerms, setVisibleTerms] = useState(false);
    const errorMsgs = useRef(null);
    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        setValue,
        getValues,
        watch
    } = useForm({ mode: 'onChange' });
    const values = getValues();
    const urlTermsPdf = '/static/TERMINI_E_CONDIZIONI.pdf'

    const onSubmit = useCallback((formData) => {
        errorMsgs.current.clear();
        setLoading(true);
        let newFormData = {
            ...formData,
            roleId: 6,
            hubUuid: APP_HUB_ID
        }

        if (!isNil(invitationData) && invitationData.isInvitation) {
            newFormData.isInvitation = true;
            AuthenticationService.registerUser(newFormData, regCallback, regError, [
                ['tempToken', invitationData.token]
            ]);
        } else {
            AuthenticationService.registerUser(newFormData, regCallback, regError);
        }
    }, [invitationData]);

    const regCallback = useCallback((data) => {
        // Il caricamento si spegne per primo: se qualcosa va storto piu' sotto,
        // l'utente non resta davanti a una rotella che gira per sempre.
        setLoading(false);
        try {
            if (data && data.status === 'SUCCESS' && data.data && data.data.token) {
                storeSet('setAuthData', {
                    token: data.data.token,
                    userData: data.data.user
                });
                storeSet('isRedirectedOnceNoCompany', true);
                window.location.replace('/primo-accesso');
                return;
            }
            // Il messaggio puo' non esserci: senza un testo di riserva la pagina
            // restava muta e sembrava bloccata.
            const dettaglio = (data && data.data && data.data.message)
                || (data && data.message)
                || __('Registrazione non riuscita. Riprova, e se il problema persiste scrivi a qualifiche@gruppohtr.com.', 'htr');
            errorMsgs.current.show([
                { sticky: true, severity: 'error', summary: '', detail: dettaglio, closable: true }
            ]);
        } catch (e) {
            errorMsgs.current.show([
                {
                    sticky: true, severity: 'error', summary: '',
                    detail: __('Registrazione non riuscita per un errore imprevisto. Riprova.', 'htr'),
                    closable: true
                }
            ]);
        }
    }, [invitationData]);

    const regError = (err) => {
        setLoading(false);
        errorMsgs.current.show([
            {
                sticky: true, severity: 'error', summary: '',
                detail: (err && err.message)
                    || __('Registrazione non riuscita. Controlla i dati inseriti e riprova.', 'htr'),
                closable: true
            }
        ]);
    }

    /*const disableAllChecks = () => {
        setValue('offers', false);
        setValue('thirdParty', false);
    }

    const enableAllChecks = () => {
        setValue('offers', true);
        setValue('thirdParty', true);
    }

    const openTermsDoc = (e) => {
        e.preventDefault();
        setVisibleTerms(true);
    }*/

    const hideTerms = () => {
        setVisibleTerms(false);
    }

    useEffect(() => {
        if (!isEmpty(token)) {
            setLoading(true);
            window.location.replace('/')
        }
    }, [token]);

    useEffect(() => {
        if (!isNil(invitationData) && invitationData.email) {
            setValue('email', invitationData.email);
        }
    }, [invitationData]);

    return (
        <div className={classNames(['appPage', 'appPageLogin'])}>
            <div className="appPageLogin__wrapper">
                <LogoIcon/>

                <h1>{__('Completamento anagrafica personale', 'htr')}</h1>
                <span>{__('Per favore, inserisci i tuoi dati.', 'htr')}</span>

                <div className="appPage__spacer"></div>

                <Messages ref={errorMsgs}/>

                <form className="appForm" onSubmit={handleSubmit(onSubmit)}>
                    <FormField
                        type="textinput"
                        fieldName="email"
                        label={__('Email', 'htr')}
                        control={control}
                        errors={errors}
                        disabled={!isNil(invitationData) && invitationData.email}
                        config={{
                            required: __('È obbligatorio', 'htr'),
                            validate: {
                                isEmail
                            }
                        }}
                        placeholder="sample@example.com"
                    />

                    <FormField
                        type="password"
                        fieldName="password"
                        label={__('Inserisci Password', 'htr')}
                        control={control}
                        errors={errors}
                        config={{
                            required: __('È obbligatorio', 'htr'),
                            minLength: { value: 8, message: __('La password deve essere di almeno 8 caratteri', 'htr') },
                            validate: {
                                hasNumber: (v) => /\d/.test(v) || __('La password deve contenere almeno un numero', 'htr'),
                                hasSpecialChar: (v) => /[~`¿¡!#$%^&*€£@+÷=\-[\]\\';,/{}()|\\": <>?._]/.test(v) || __('La password deve contenere almeno un carattere speciale', 'htr'),
                                hasCapital: (v) => /[A-Z]/.test(v) || __('La password deve contenere almeno una lettera maiuscola', 'htr'),
                            }
                        }}
                    />
                    <PasswordChecklist
                        value={watch('password') || ''}
                        rules={['minLength', 'number', 'specialChar', 'capital']}
                        minLength={8}
                        messages={{
                            minLength: __('La password deve avere almeno 8 caratteri', 'htr'),
                            number: __('La password deve contenere almeno un numero', 'htr'),
                            specialChar: __('La password deve contenere almeno un carattere speciale', 'htr'),
                            capital: __('La password deve contenere almeno una lettera maiuscola', 'htr'),
                        }}
                    />

                    <FormField
                        type="password"
                        fieldName="confPassword"
                        label={__('Conferma Password', 'htr')}
                        control={control}
                        errors={errors}
                        config={{
                            required: __('È obbligatorio', 'htr'),
                            validate: (value) => {
                                const password = watch('password');
                                return value === password || __('Le password non corrispondono', 'htr');
                            }
                        }}
                    />

                    <Panel
                        headerTemplate={(options) => {
                            return (
                                <div className={classNames(['p-panel-header', 'reverseSwitchField'])}>
                                    <FormField
                                        type="switch"
                                        fieldName="privacy"
                                        label={__('Ho letto e accetto l’Informativa sulla Privacy (Necessario)', 'htr')}
                                        control={control}
                                        errors={errors}
                                        defaultValue={values['privacy']}
                                        onLabel={''}
                                        offLabel={''}
                                        config={{
                                            required: __('È obbligatorio', 'htr'),
                                        }}
                                    />
                                    <div>
                                        {options.togglerElement}
                                    </div>
                                </div>
                            );
                        }}
                        toggleable>
                        <p className="m-0">
                            {__('HTR BONIFICHE S.r.l., per quanto di ragione, società con sede legale in Via Bassano del Grappa, 4 – 00195 Roma, Italia (di seguito, “HTR” o il “Titolare”), informano che i dati forniti da ciascuno, sia nell’utilizzo di questo sito web, sia nelle circostanze più avanti specificate, saranno oggetto di trattamento nei termini di seguito descritti, che costituiscono “informativa” ai sensi dell’art. 13 del Regolamento Generale sulla Protezione dei Dati Personali 2016/679 (di seguito, “GDPR” o “Regolamento”), nonché del D.Lgs. 30.6.2003 n. 196, come modificato dal D.Lgs. n. 101/2018.', 'htr')}
                        </p>
                        <p>
                            <a href="https://gruppohtr.com/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>
                        </p>
                    </Panel>

                    <Panel
                        headerTemplate={(options) => {
                            return (
                                <div className={classNames(['p-panel-header', 'reverseSwitchField'])}>
                                    <FormField
                                        type="switch"
                                        fieldName="terms"
                                        label={__('Accetto Terms & Conditions', 'htr')}
                                        control={control}
                                        errors={errors}
                                        defaultValue={values['terms']}
                                        onLabel={''}
                                        offLabel={''}
                                        config={{
                                            required: __('È obbligatorio', 'htr'),
                                        }}
                                    />
                                    <div>
                                        {options.togglerElement}
                                    </div>
                                </div>
                            );
                        }}
                        toggleable>
                        <p className="m-0">
                            <a href={urlTermsPdf} target="_blank" rel="noreferrer">{__('Termini e condizioni', 'htr')}</a>
                        </p>
                    </Panel>

                    {/*<Panel
                        header={__('Altri consensi (facoltativo)', 'htr')}
                        toggleable>
                        <div className="appForm__col">
                            <div className={classNames(['appForm__row', 'reverseSwitchField'])}>
                                <FormField
                                    type="switch"
                                    fieldName="marketing"
                                    label={''}
                                    control={control}
                                    errors={errors}
                                    defaultValue={values['marketing']}
                                    onLabel={''}
                                    offLabel={''}
                                />
                                <div>
                                    {__('Invio di materiale pubblicitario, vendita diretta, compimento di ricerche di mercato o comunicazione commerciale riguardanti promozione e vendita di prodotti e servizi della HTR, mediante modalità di contatto automatizzate (posta elettronica, PEC, messaggi tramite canali informatici, network ed applicazioni web) e tradizionali (come posta cartacea e chiamate telefoniche con operatore)', 'htr')}
                                </div>
                            </div>
                            <div className={classNames(['appForm__row', 'reverseSwitchField'])}>
                                <FormField
                                    type="switch"
                                    fieldName="offers"
                                    label={''}
                                    control={control}
                                    errors={errors}
                                    defaultValue={values['offers']}
                                    onLabel={''}
                                    offLabel={''}
                                />
                                <div>
                                    {__('Elaborazione, in forma elettronica, dei dati relativi ai rapporti e servizi forniti, per l’analisi di comportamenti e preferenze della clientela, da utilizzare a scopo commerciale, per la individuazione ed offerta di prodotti e servizi di suo interesse', 'htr')}
                                </div>
                            </div>
                            <div className={classNames(['appForm__row', 'reverseSwitchField'])}>
                                <FormField
                                    type="switch"
                                    fieldName="thirdParty"
                                    label={''}
                                    control={control}
                                    errors={errors}
                                    defaultValue={values['thirdParty']}
                                    onLabel={''}
                                    offLabel={''}
                                />
                                <div>
                                    {__('Comunicazione dei miei dati ad altre società in ambito bancario, finanziario od assicurativo e enti pubblici che li tratteranno per invio di materiale pubblicitario, vendita diretta, compimento di ricerche di mercato o comunicazione commerciale riguardanti loro prodotti o servizi, mediante le modalità automatizzate e tradizionali di comunicazione sopra indicate', 'htr')}
                                </div>
                            </div>
                            <div className="appForm__cols">
                                <Button
                                    type="button"
                                    severity="info"
                                    onClick={disableAllChecks}
                                    outlined
                                    label={__('Nega tutti', 'htr')}/>

                                <Button
                                    type="button"
                                    severity="info"
                                    onClick={enableAllChecks}
                                    label={__('Attiva tutti', 'htr')}/>
                            </div>
                        </div>
                    </Panel>*/}

                    <Button
                        label={__('Invia', 'htr')}
                        disabled={loading || !isValid}/>
                </form>

                <Dialog header={__('Termini e condizioni', 'htr')} visible={visibleTerms} style={{
                    width: '70vw',
                    minHeight: 500
                }} onHide={hideTerms}>
                    <iframe src={urlTermsPdf} title={__('Termini e condizioni', 'htr')}></iframe>
                </Dialog>
            </div>
        </div>
    )
}

export default Registration;